/* ──────────────────────────────────────────────────────────────
   src/pages/reports/clientes/RetrasosAlumnoPage.tsx
   ────────────────────────────────────────────────────────────── */
import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  TextField,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  LinearProgress,
  Alert,
  InputAdornment,
  Button,
  Chip,
  Box,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { useRetrasosAlumnoReport } from '../../../hooks/useReportJob';
import type { RetrasosAlumnoRow } from '../../../api/reports';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

export default function RetrasosAlumnoPage() {
  const { t } = useTranslation<'translation'>();
  /* ═════════ filtros de entrada ═════════ */
  const today = dayjs().format('YYYY-MM-DD');
  const [desde, setDesde] = useState(today);
  const [hasta, setHasta] = useState(today);
  const [search, setSearch] = useState('');

  /* ═════════ job resumen (retrasos por alumno) ═════════ */
  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useRetrasosAlumnoReport();
  const { data: status } = useStatus(jobId);

  /* ───── generar informe ───── */
  const launch = async () => {
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error('Error al descargar:', error);
      }
    }
  };

  /* ───── resumen filtrado por búsqueda ───── */
  const rows =
    status?.result?.rows.filter((r: RetrasosAlumnoRow) =>
      r.alumno.toLowerCase().includes(search.toLowerCase()),
    ) ?? [];

  /* ═════════ helpers UI ═════════ */
  const formatMinutos = (min: number) => {
    const horas = (Number(min) || 0) / 60;
    return horas.toLocaleString('en-US', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const getTasaColor = (tasa: number) => {
    if (tasa >= 20) return 'error';
    if (tasa >= 10) return 'warning';
    return 'success';
  };

  /* ═════════ UI ═════════ */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('retrasosAlumno.titulo', 'Retrasos de Alumnos')}</Typography>

      {/* ─── barra de filtros ─── */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          type="date"
          label={t('retrasosAlumno.filtros.desde', 'Desde')}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          label={t('retrasosAlumno.filtros.hasta', 'Hasta')}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          placeholder={t('retrasosAlumno.buscar', 'Buscar alumno...') as string}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: 300 }}
        />
        <Button
          variant="contained"
          onClick={launch}
          disabled={!desde || !hasta || status?.progress === 100}
          startIcon={<SearchIcon />}
        >
          {t('retrasosAlumno.generar', 'Generar Informe')}
        </Button>
      </Stack>

      {/* ─── progreso ─── */}
      {status && !status.done && !status.failed && (
        <Box>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {t('retrasosAlumno.procesando', 'Procesando...')} {status.progress}%
          </Typography>
        </Box>
      )}

      {/* ─── error ─── */}
      {status?.failed && (
        <Alert severity="error">
          {t('retrasosAlumno.error', 'Error al generar el informe. Por favor, inténtalo de nuevo.')}
        </Alert>
      )}

      {/* ─── resultados ─── */}
      {status?.done && status.result && (
        <Stack spacing={2}>
          {/* ─── resumen ─── */}
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {t('retrasosAlumno.resultados', 'Resultados')}
              </Typography>
              <Chip
                label={`${status.result.registros} ${t('retrasosAlumno.alumnos', 'alumnos')}`}
                color="primary"
                variant="outlined"
              />
              {status.result.url && (
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  variant="outlined"
                >
                  {t('retrasosAlumno.descargar', 'Descargar Excel')}
                </Button>
              )}
            </Stack>
          </Paper>

          {/* ─── tabla ─── */}
          {rows.length > 0 ? (
            <Paper>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>{t('retrasosAlumno.tabla.alumno', 'Alumno')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosAlumno.tabla.numRetrasos', 'Nº Retrasos')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosAlumno.tabla.tiempoTotal', 'Tiempo Total')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosAlumno.tabla.tiempoPromedio', 'Promedio')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosAlumno.tabla.tiempoMaximo', 'Máximo')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosAlumno.tabla.tasaRetrasos', 'Tasa Retrasos')}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{t('retrasosAlumno.tabla.totalReservas', 'Total Reservas')}</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row: RetrasosAlumnoRow) => (
                    <TableRow key={row.alumnoId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {row.alumno}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={row.numRetrasos}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={`${row.tiempoTotalMin} minutos`}>
                          <Typography variant="body2" fontWeight="medium">
                            {formatMinutos(row.tiempoTotalMin)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {formatMinutos(Math.round(row.tiempoPromedioMin))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {formatMinutos(row.tiempoMaxMin)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${row.tasaRetrasos.toFixed(1)}%`}
                          size="small"
                          color={getTasaColor(row.tasaRetrasos)}
                          variant={row.tasaRetrasos >= 10 ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {row.totalReservas}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Alert severity="info">
              {t(
                'retrasosAlumno.sinResultados',
                'No se encontraron retrasos en el periodo seleccionado.',
              )}
            </Alert>
          )}
        </Stack>
      )}
    </Stack>
  );
}
